<template>
  <div class="advanced-ops">
    <h4 class="page-title">Advanced Operations</h4>

    <!-- ===================== Logo Upload ===================== -->
    <el-card class="section" shadow="never">
      <template #header>
        <span class="section-title">Logo Upload</span>
      </template>
      <div class="logo-row">
        <el-upload
          action=""
          :http-request="uploadLogo"
          :auto-upload="false"
          :on-change="onFileChange"
          :show-file-list="false"
        >
          <el-button type="primary">Choose File</el-button>
        </el-upload>
        <span class="file-name">{{ selectedFile ? selectedFile.name : 'No file selected' }}</span>
        <el-button type="success" :loading="logoUploading" @click="uploadLogo">Upload</el-button>
      </div>
    </el-card>

    <!-- ===================== Generation Mode ===================== -->
    <el-card class="section" shadow="never">
      <template #header>
        <span class="section-title">Certificate Generation Mode</span>
      </template>

      <div class="mode-current">
        Current mode:
        <el-tag :type="mode === 'pregen' ? 'warning' : 'success'" effect="dark">
          {{ mode === 'pregen' ? 'Pre-generation (cached)' : 'Real-time' }}
        </el-tag>
      </div>

      <!-- Intro & comparison -->
      <div class="mode-compare">
        <div class="mode-box">
          <h5>Real-time Mode</h5>
          <ul>
            <li>Certificates are generated on the fly every time a parent queries.</li>
            <li>No image is stored on disk; nothing to maintain.</li>
            <li>Higher CPU cost per request; slower when many people query at once.</li>
          </ul>
        </div>
        <div class="mode-box">
          <h5>Pre-generation Mode</h5>
          <ul>
            <li>Certificates are rendered ahead of time and cached under the shared <code>cache</code> directory.</li>
            <li>Parent queries and admin previews serve the cached image directly (fast); a missing one is generated once and then cached.</li>
            <li>You must run "Pre-generate" on the Student Query page after importing new students.</li>
            <li>Turning this mode OFF deletes ALL cached images.</li>
          </ul>
        </div>
      </div>

      <div class="mode-actions">
        <el-button
          v-if="mode !== 'pregen'"
          type="warning"
          :loading="modeLoading"
          @click="openEnableDialog"
        >
          Enable Pre-generation Mode
        </el-button>
        <el-button
          v-else
          type="danger"
          :loading="modeLoading"
          @click="openDisableDialog"
        >
          Switch back to Real-time Mode
        </el-button>
        <el-button text :loading="modeLoading" @click="fetchMode">Refresh</el-button>
      </div>
    </el-card>

    <!-- ===================== Query Open Time ===================== -->
    <el-card class="section" shadow="never">
      <template #header>
        <span class="section-title">Query Open Time</span>
      </template>

      <div class="mode-current">
        Status:
        <el-tag :type="queryTimeEnabled ? 'warning' : 'success'" effect="dark">
          {{ queryTimeEnabled ? ('Restricted — opens at ' + queryTime) : 'Open anytime' }}
        </el-tag>
      </div>

      <div class="mode-box" style="margin-bottom:16px;">
        <ul>
          <li>When enabled, parents can only query <b>after</b> the chosen date &amp; time.</li>
          <li>Before that time, any query attempt is rejected with a notice to wait until it opens.</li>
          <li>When disabled, queries are allowed at any time.</li>
        </ul>
      </div>

      <div class="qt-row">
        <el-date-picker
          v-model="queryTimeInput"
          type="datetime"
          placeholder="Select open date & time"
          format="YYYY-MM-DD HH:mm:ss"
          value-format="YYYY-MM-DD HH:mm:ss"
        />
        <el-button type="warning" :loading="queryTimeLoading" @click="enableQueryTime">
          Enable / Update
        </el-button>
        <el-button
          type="danger"
          :loading="queryTimeLoading"
          :disabled="!queryTimeEnabled"
          @click="disableQueryTime"
        >
          Disable
        </el-button>
        <el-button text :loading="queryTimeLoading" @click="fetchQueryTime">Refresh</el-button>
      </div>
    </el-card>

    <!-- ===================== Copyright Notice Display ===================== -->
    <el-card class="section" shadow="never">
      <template #header>
        <span class="section-title">版权声明显示</span>
      </template>

      <div class="mode-current">
        当前状态：
        <el-tag :type="copyrightDisplayEnabled ? 'success' : 'info'" effect="dark">
          {{ copyrightDisplayEnabled ? '家长查询页已显示' : '家长查询页已隐藏' }}
        </el-tag>
      </div>

      <div class="mode-box" style="margin-bottom:16px;">
        <ul>
          <li>开启后，家长查询页面底部会显示开发者、美工设计和版权声明。</li>
          <li>关闭后，家长查询页面底部不显示该版权声明区域。</li>
        </ul>
      </div>

      <div class="copyright-row">
        <span class="setting-label">显示版权声明</span>
        <el-switch
          v-model="copyrightDisplayEnabled"
          :loading="copyrightDisplayLoading"
          active-text="显示"
          inactive-text="隐藏"
          @change="saveCopyrightDisplay"
        />
        <el-button text :loading="copyrightDisplayLoading" @click="fetchCopyrightDisplay">Refresh</el-button>
      </div>
    </el-card>

    <!-- Enable dialog -->
    <el-dialog v-model="enableDialog" title="Enable Pre-generation Mode" width="460px" align-center>
      <el-alert
        title="Dangerous operation"
        type="warning"
        :closable="false"
        description="This changes how certificates are produced system-wide. Type confirm below to proceed."
        show-icon
      />
      <div class="dialog-field">
        <label>Type <b>confirm</b> to continue:</label>
        <el-input v-model="confirmWord" placeholder="confirm" />
      </div>
      <template #footer>
        <el-button @click="enableDialog = false">Cancel</el-button>
        <el-button type="warning" :loading="modeLoading" @click="submitEnable">Confirm</el-button>
      </template>
    </el-dialog>

    <!-- Disable dialog -->
    <el-dialog v-model="disableDialog" title="Switch to Real-time Mode" width="460px" align-center>
      <el-alert
        title="Dangerous operation — cache will be deleted"
        type="error"
        :closable="false"
        description="Switching back to real-time mode will DELETE ALL pre-generated cached images. This cannot be undone."
        show-icon
      />
      <div class="dialog-field">
        <label>Type <b>confirm</b>:</label>
        <el-input v-model="confirmWord" placeholder="confirm" />
      </div>
      <div class="dialog-field">
        <label>Type <b>close</b> to delete all cache:</label>
        <el-input v-model="closeWord" placeholder="close" />
      </div>
      <template #footer>
        <el-button @click="disableDialog = false">Cancel</el-button>
        <el-button type="danger" :loading="modeLoading" @click="submitDisable">Confirm</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import axios from 'axios';

export default {
  name: 'AdvancedOperations',
  data() {
    return {
      // logo
      selectedFile: null,
      logoUploading: false,
      // mode
      mode: '',
      modeLoading: false,
      enableDialog: false,
      disableDialog: false,
      confirmWord: '',
      closeWord: '',
      // query time
      queryTimeEnabled: false,
      queryTime: '',
      queryTimeInput: '',
      queryTimeLoading: false,
      // copyright display
      copyrightDisplayEnabled: true,
      copyrightDisplayLoading: false,
    };
  },
  mounted() {
    this.fetchMode();
    this.fetchQueryTime();
    this.fetchCopyrightDisplay();
  },
  methods: {
    onFileChange(file) {
      this.selectedFile = file.raw;
    },
    async uploadLogo() {
      if (!this.selectedFile) {
        ElMessage.error('Please select a file first');
        return;
      }
      const formData = new FormData();
      formData.append('logo', this.selectedFile);
      this.logoUploading = true;
      try {
        const response = await axios.post('/upload/logo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.status === 200) {
          ElMessage.success('Logo uploaded successfully');
        } else {
          ElMessage.error('Failed to upload logo');
        }
      } catch (error) {
        console.error('Error uploading logo:', error);
        ElMessage.error('Failed to upload logo');
      } finally {
        this.logoUploading = false;
      }
    },
    async fetchMode() {
      this.modeLoading = true;
      try {
        const res = await axios.get('/api/gen-mode');
        this.mode = res.data.mode;
      } catch (e) {
        ElMessage.error('Failed to load generation mode');
      } finally {
        this.modeLoading = false;
      }
    },
    openEnableDialog() {
      this.confirmWord = '';
      this.enableDialog = true;
    },
    openDisableDialog() {
      this.confirmWord = '';
      this.closeWord = '';
      this.disableDialog = true;
    },
    async submitEnable() {
      if (this.confirmWord !== 'confirm') {
        ElMessage.warning('Please type confirm exactly');
        return;
      }
      this.modeLoading = true;
      try {
        await axios.post('/api/gen-mode', { mode: 'pregen', confirm: this.confirmWord });
        this.mode = 'pregen';
        this.enableDialog = false;
        ElMessage.success('Pre-generation mode enabled. Please pre-generate on the Student Query page.');
      } catch (e) {
        ElMessage.error(e.response?.data?.error || 'Failed to switch mode');
      } finally {
        this.modeLoading = false;
      }
    },
    async submitDisable() {
      if (this.confirmWord !== 'confirm') {
        ElMessage.warning('Please type confirm exactly');
        return;
      }
      if (this.closeWord !== 'close') {
        ElMessage.warning('Please type close exactly');
        return;
      }
      this.modeLoading = true;
      try {
        const res = await axios.post('/api/gen-mode', {
          mode: 'realtime',
          confirm: this.confirmWord,
          close: this.closeWord,
        });
        this.mode = 'realtime';
        this.disableDialog = false;
        ElMessage.success(`Switched to real-time mode. Deleted ${res.data.removed ?? 0} cached images.`);
      } catch (e) {
        ElMessage.error(e.response?.data?.error || 'Failed to switch mode');
      } finally {
        this.modeLoading = false;
      }
    },
    async fetchQueryTime() {
      this.queryTimeLoading = true;
      try {
        const res = await axios.get('/api/query-time');
        this.queryTimeEnabled = res.data.enabled;
        this.queryTime = res.data.time || '';
        if (res.data.time) this.queryTimeInput = res.data.time;
      } catch (e) {
        ElMessage.error('Failed to load query time');
      } finally {
        this.queryTimeLoading = false;
      }
    },
    async enableQueryTime() {
      if (!this.queryTimeInput) {
        ElMessage.warning('Please select a date & time first');
        return;
      }
      this.queryTimeLoading = true;
      try {
        const res = await axios.post('/api/query-time', {
          enabled: true,
          time: this.queryTimeInput,
        });
        this.queryTimeEnabled = res.data.enabled;
        this.queryTime = res.data.time;
        ElMessage.success(`Query will open at ${res.data.time}`);
      } catch (e) {
        ElMessage.error(e.response?.data?.error || 'Failed to set query time');
      } finally {
        this.queryTimeLoading = false;
      }
    },
    async disableQueryTime() {
      this.queryTimeLoading = true;
      try {
        await axios.post('/api/query-time', { enabled: false });
        this.queryTimeEnabled = false;
        this.queryTime = '';
        ElMessage.success('Query time restriction disabled. Queries are open anytime.');
      } catch (e) {
        ElMessage.error(e.response?.data?.error || 'Failed to disable query time');
      } finally {
        this.queryTimeLoading = false;
      }
    },
    async fetchCopyrightDisplay() {
      this.copyrightDisplayLoading = true;
      try {
        const res = await axios.get('/api/copyright-display');
        this.copyrightDisplayEnabled = res.data.enabled !== false;
      } catch (e) {
        ElMessage.error('Failed to load copyright display setting');
      } finally {
        this.copyrightDisplayLoading = false;
      }
    },
    async saveCopyrightDisplay(enabled) {
      this.copyrightDisplayLoading = true;
      try {
        const res = await axios.post('/api/copyright-display', { enabled });
        this.copyrightDisplayEnabled = res.data.enabled;
        ElMessage.success(enabled ? '版权声明已显示' : '版权声明已隐藏');
      } catch (e) {
        this.copyrightDisplayEnabled = !enabled;
        ElMessage.error(e.response?.data?.error || 'Failed to save copyright display setting');
      } finally {
        this.copyrightDisplayLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.advanced-ops {
  max-width: 900px;
  margin: 0 auto;
  padding: 16px;
}
.page-title {
  text-align: center;
  margin-bottom: 20px;
}
.section {
  margin-bottom: 20px;
}
.section-title {
  font-weight: 600;
}
.logo-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.file-name {
  color: #909399;
  font-size: 13px;
}
.mode-current {
  margin-bottom: 14px;
  font-size: 15px;
}
.mode-compare {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.mode-box {
  flex: 1;
  min-width: 260px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 16px;
  background: #fafafa;
}
.mode-box h5 {
  margin: 0 0 8px;
}
.mode-box ul {
  margin: 0;
  padding-left: 18px;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}
.mode-box code {
  background: #eee;
  padding: 0 4px;
  border-radius: 3px;
}
.mode-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}
.dialog-field {
  margin-top: 14px;
}
.dialog-field label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: #303133;
}
.qt-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.copyright-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.setting-label {
  font-size: 14px;
  color: #303133;
}
</style>
